import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";
import { Tab } from "@headlessui/react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  singlePolicyViewLinks,
  annuitySinglePolicyViewLinks,
} from "js/placeholderData/agents/singlePolicyViewLinks";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { usePolicyMapper } from "hooks/usePolicyMapper";
import { useClaimsMapper } from "hooks/useClaimsMapper";
import { setOwner } from "redux/slices/ownerSlice";
import { getName } from "js/formatting";
import { getPaymentHistory } from "redux/slices/getPaymentHistorySlice";
import {
  getPrimaryOwnerFromParties,
  getTypeOfPolicy,
  isWholeLifePolicy,
} from "js/utilities";
import { useTranslation } from "react-i18next";
import Toast from "components/General/Toast";
import SinglePolicyView from "components/AgentPortal/PolicyDetails/SinglePolicyView";
import PaymentTab from "components/AgentPortal/PolicyDetails/PaymentTab";
import HelpfulLinks from "components/AgentPortal/General/HelpfulLinks";
import ServiceAndClaimsTab from "components/AgentPortal/PolicyDetails/ServiceAndClaimsTab";
import PolicyLookupModal from "components/AgentPortal/ModalDialogs/PolicyLookupModal";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";

const PolicyDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { policy_id } = useParams();
  const { agentPolicies: policies } = useContext(AgentData);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const [payment, setPayment] = useState({});
  const [policyId, setPolicyId] = useState(policy_id);
  const [selectedPolicyId, setSelectedPolicyId] = useState({
    value: policy_id,
  });
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);
  const [isFinancialsLoading, setIsFinancialsLoading] = useState(false);
  const { policy, policyDetail, filteredDocuments, error } =
    usePolicyMapper(policyId);

  const { claims } = useClaimsMapper();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClaims, setSelectedClaims] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    setIsFinancialsLoading(true);
    if (error) {
      setIsLoading(false);
      setOpenDialog(true);
    }

    const primaryOwner = getPrimaryOwnerFromParties(
      policyDetail?.parties ?? []
    );
    dispatch(setOwner({ taxId: primaryOwner?.person?.taxId }));
    setIsPaymentLoading(true);
    dispatch(
      getPaymentHistory([
        {
          contractId: policy_id,
          policyName: getName(primaryOwner?.person),
          policyNumber: policyDetail?.contractID,
          oldPolicy: policyDetail?.oldPolicy,
        },
      ])
    )
      .then((response) => {
        setIsLoading(false);
        if (response.meta.requestStatus === "fulfilled") {
          setPayment({
            paymentHistories: response?.payload,
          });
          setIsFinancialsLoading(false);
          setIsPaymentLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setIsPaymentLoading(false);
        console.log("Error", error?.message);
      });
    let policySelectionArrayForDocTab = [];
    policies.map((p) => {
      policySelectionArrayForDocTab.push({
        value: p.contractID,
        displayAsValue: p.oldPolicy,
        displayAs: `${p.oldPolicy}: ${p?.insuredFullName}`,
        ...p,
      });
      setPolicyFilterOptions([
        {
          value: "all",
          displayAs: t("AgentContractDetails-all-contracts"),
          status: "",
          displayAsValue: t("AgentContractDetails-all-contracts"),
        },
        ...policySelectionArrayForDocTab,
      ]);
    });
    setSelectedClaims(claims?.filter((x) => x.oldPolicy === policy?.oldPolicy));
  }, [dispatch, policies, policy, error]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  let [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    let tab = searchParams.get("tab");
    switch (tab) {
      case "policy":
        setSelectedTabIndex(0);
        break;

      case "payment":
        setSelectedTabIndex(1);
        break;

      case "claims":
        setSelectedTabIndex(2);
        break;

      default:
        break;
    }
  }, [searchParams]);

  useBreadcrumb([
    { name: t("AgentClaims-home"), url: "/dashboard" },
    { name: t("AgentBookOfBusiness-title1"), url: "/book-of-business" },
    { name: t("AgentPolicyDetails-contract-title"), url: "#" },
  ]);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("AgentPolicyDetails-contract-title")}</h1>
        <p>{t("AgentPolicyDetails-subtitle")}</p>
        <hr />
      </div>

      {selectedTabIndex === 1 && (
        <div className="mb-8">
          <Toast
            title={t("Toast-paymentTitle")}
            message={t("Toast-paymentSubtitle")}
          />
        </div>
      )}

      <div className="container mb-6">
        <div className="md:w-1/2">
          <AgentAutoCompleteComboBox
            labelName={t("AgentAutoComplate-display-as")}
            inputPlaceholderText={t("AgentFACStepOne-policyselect")}
            options={policyFilterOptions}
            selectedOption={selectedPolicyId}
            setSelectedOption={(option) => {
              setSelectedPolicyId(option?.value);
              if (option?.value !== "all") {
                setPolicyId(option.contractID);
                navigate(`/policies/${option.contractID}`);
              } else {
                navigate("/book-of-business");
              }
            }}
          />
        </div>
      </div>

      <div className="container">
        <Tab.Group selectedIndex={selectedTabIndex}>
          <div className="mb-6">
            <div className="ui-card flex-grow px-2 p-1 md:px-4">
              <Tab.List className="flex flex-row whitespace-nowrap">
                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 0 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSearchParams("?tab=policy");
                    setSelectedTabIndex(0);
                  }}
                >
                  {t("AgentPolicyDetails-contract-title")}
                </Tab>
                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 1 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSearchParams("?tab=payment");
                    setSelectedTabIndex(1);
                  }}
                >
                  {t("AgentPolicyDetails-tab-2-title")}
                </Tab>
                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 2 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSearchParams("?tab=claims");
                    setSelectedTabIndex(2);
                  }}
                >
                  {t("AgentPolicyDetails-tab-3-title")}
                </Tab>
              </Tab.List>
            </div>
          </div>

          <Tab.Panels className="mt-6">
            <Tab.Panel>
              <EverlakeLoadingOverlay
                isLoading={isLoading || isFinancialsLoading}
                loadingText={t("AgentPolicyDetails-tab-1-loadingtext")}
              >
                <div className="grid gap-4 grid-cols-1 lg:grid-cols-[66.666%,1fr] xl:gap-6">
                  <div>
                    <SinglePolicyView
                      policy={policy}
                      policyDetail={policyDetail}
                      filteredDocuments={filteredDocuments}
                      deathBenefitValue={
                        policyDetail.financials?.cashValues[0]?.deathBenefit
                      }
                    />
                  </div>
                  <div>
                    <HelpfulLinks
                      policy={policy}
                      links={
                        getTypeOfPolicy(policy?.product?.type) === "annuity"
                          ? annuitySinglePolicyViewLinks
                          : singlePolicyViewLinks.map((section) => ({
                              ...section,
                              links: section.links.filter((link) => {
                                if (link.name !== "Make a Payment") return true;
                                const productType = policy?.product?.type;
                                return !["LZ", "LQ", "LOP"].includes(
                                  productType
                                );
                              }),
                            }))
                      }
                    />
                  </div>
                </div>
              </EverlakeLoadingOverlay>
            </Tab.Panel>

            <Tab.Panel>
              <EverlakeLoadingOverlay
                isLoading={isPaymentLoading}
                loadingText={t("AgentPolicyDetails-tab-2-loadingText")}
              >
                <PaymentTab payment={payment} policy={policy} />
              </EverlakeLoadingOverlay>
            </Tab.Panel>

            <Tab.Panel>
              <ServiceAndClaimsTab claims={selectedClaims} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
      <GeneralPurposeDialog
        state={openDialog}
        setState={setOpenDialog}
        title={t("AgentPolicyDetails-dialogTitle")}
        desc={t("AgentPolicyDetails-dialogDesc")}
        secondaryButtonAction={() => {
          setOpenDialog(false);
          navigate("/book-of-business");
        }}
      />
    </>
  );
};

export default PolicyDetails;
